import { useGLTF, useAnimations } from "@react-three/drei";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { SkeletonUtils } from "three/examples/jsm/Addons.js";
import clickSound from '../components/sceneComponents/click_Sound.mp3';
import { useModal2D } from "../contexts/Modal2DContext";
import { Select } from "@react-three/postprocessing";
import AboutMe from "../components/billboard_components/AboutMe";

const clickSoundInstance = new Audio(clickSound);
clickSoundInstance.volume = 0.5;

const Character2D = () => {
    const group = useRef();
    const {theme} = useTheme();
    const [ isHovered, setIsHovered ] = useState(false);
    const { isOpen, openModal } = useModal2D();

    const { scene, animations } = useGLTF("./mage_compressed-v1.glb", true, false, (loader) => {
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.5/');
        dracoLoader.setDecoderConfig({ type: 'js' });
        loader.setDRACOLoader(dracoLoader);
    });

    // clone the scene to not overlap the cached reg char
    const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);

    // Can probably compress the mage character model even more by 
    // keeping only necessary action animations
    const { actions } = useAnimations(animations, group);
    
    // Add shadows to entire character model
    useEffect(() => {
        clonedScene.traverse((child) => {
            if(child.isMesh){
                child.castShadow = true;
                child.receiveShadow = true;
            }
        })
    }, [clonedScene]);
    
    // Idle Animation
    useEffect(() => {
        actions["Idle"].reset().fadeIn(0.24).play();
    }, [actions]);

    const playClickSound = () => {
        clickSoundInstance.currentTime = 0; 
        clickSoundInstance.play();
    }

    const handleClick = useCallback((e) => {
        e.stopPropagation();
        playClickSound();

        // Open UI
        openModal(<AboutMe />)
    }, []);

    // Add outline hover effect
    const handlePointerOver = useCallback((e) => {
        e.stopPropagation();
        setIsHovered(true);
        document.body.style.cursor = 'pointer';
    }, []);
    
    // Remove outline hover effect
    const handlePointerLeave = useCallback((e) => {
        e.stopPropagation();
        setIsHovered(false);
        document.body.style.cursor = 'default';
    }, []);

    return (
        <>
            <Select enabled={isHovered}>
                <group 
                    ref={group}
                    onPointerOver={handlePointerOver}
                    onPointerOut={handlePointerLeave}
                    onClick={handleClick}
                >
                    <primitive
                        object={clonedScene}
                        scale={[0.4, 0.4, 0.4]}
                        position={[-17, -5.16, -15.8]}
                        rotation-y={Math.PI}
                    />
                    {theme === 'dark' ? (
                        <pointLight position={[-17.58, -4.26, -16.23]} intensity={10} position-y={1} color="white" />
                    ) : (<></>)}
                </group>
            </Select>
        </>
    );
}

export default Character2D;